import '@app/assets/css/globals.css';
import globalConstants from '@app/constants/global';
import { NextPageWithLayout } from '@app/types';
import { Analytics } from '@vercel/analytics/react';
import { NextSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { Caveat, Nunito_Sans } from 'next/font/google';
import Head from 'next/head';

const nunito = Nunito_Sans({ subsets: ['latin'], display: 'swap', variable: '--font-nunito' });
const caveat = Caveat({ subsets: ['latin'], weight: ['600', '700'], display: 'swap', variable: '--font-caveat' });

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
    pageProps: any;
};

export default function App({ Component, pageProps, router }: AppPropsWithLayout) {
    const getLayout = Component.getLayout ?? ((page: any) => page);
    const { title, desc, url, image } = globalConstants.socialPreview;

    return (
        <>
            {/* next/font exposes each family as a CSS variable; globals.css and
                tailwind.config.ts both read them from :root. */}
            <style jsx global>{`
                :root {
                    --font-nunito: ${nunito.style.fontFamily};
                    --font-caveat: ${caveat.style.fontFamily};
                }
            `}</style>

            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="theme-color" content="#ffffff" />
                <meta name="color-scheme" content="light" />
            </Head>

            <NextSeo
                title={title}
                description={desc}
                openGraph={{
                    type: 'website',
                    locale: 'en_IE',
                    url,
                    site_name: globalConstants.appName,
                    description: desc,
                    title,
                    images: [{ url: image, alt: globalConstants.appName }]
                }}
                twitter={{ handle: globalConstants.twitterHandle, site: url, cardType: 'summary_large_image' }}
            />

            {getLayout(<Component {...pageProps} key={router.asPath} />)}
            <Analytics />
        </>
    );
}
